import React, { useState, useEffect, useMemo, useCallback } from "react";
import Avatar from "../../components/Avatar";
import { Trash2, Plus, X, Search, Edit2, Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import {
  useGetAcademyQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetDepartmentQuery,
  useGetUsersQuery,
  useGetRolesQuery,
  useSoftDeleteUserMutation,
  useGetDivisionsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAdminResetPasswordMutation,
} from "../../slices/userApiSlice";
import { toast } from "sonner";
import { isValidEmail } from "../../utils/userManagementUtils";
import ResetPasswordModal from "../../components/UserManagement/ResetPasswordModal";

// Constants
const TABS = ["Manager", "Supervisor", "Technician", "CFM", "Admin", "Role"];
const ROWS_PER_PAGE = 10;
const CFM_ROLE_NAMES = ["cfm", "chief facility manager"];

const SAdminUser = () => {
  const navigate = useNavigate();

  // UI State
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Manager");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [deleteRoleConfirm, setDeleteRoleConfirm] = useState({
    open: false,
    id: null,
  });
  const [deleteUserConfirm, setDeleteUserConfirm] = useState({
    open: false,
    id: null,
  });

  // User Form State
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDiv, setSelectedDiv] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [emailError, setEmailError] = useState("");

  // Role Management State
  const [roleNameInput, setRoleNameInput] = useState("");
  const [roleDescriptionInput, setRoleDescriptionInput] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [editRoleId, setEditRoleId] = useState(null);

  // Password Reset State
  const [showResetModal, setShowResetModal] = useState(false);
  const [userToReset, setUserToReset] = useState(null);

  // Data State
  const [data, setData] = useState([]);

  // API Queries
  const { data: academies } = useGetAcademyQuery();
  const { data: department } = useGetDepartmentQuery();
  const {
    data: users,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useGetUsersQuery();
  const { data: roles } = useGetRolesQuery();
  const { data: divisions } = useGetDivisionsQuery();

  // API Mutations
  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const [softDeleteUser] = useSoftDeleteUserMutation();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();
  const [adminResetPassword, { isLoading: isResettingPassword }] =
    useAdminResetPasswordMutation();

  // Helper functions
  const matchesAcademyFilter = useCallback((item, academyId) => {
    if (!academyId || academyId === "all") return true;
    return item.academyId === academyId || item._id === academyId;
  }, []);

  const matchesSearchTerm = useCallback((item, searchTerm) => {
    if (!item) return false;
    const lowerSearch = searchTerm.toLowerCase();
    return Object.values(item).some((v) =>
      v ? String(v).toLowerCase().includes(lowerSearch) : false,
    );
  }, []);

  const isCFMRole = useCallback((roleName) => {
    if (!roleName) return false;
    return CFM_ROLE_NAMES.includes(roleName.toLowerCase());
  }, []);

  const getRoleIdByTab = useCallback(
    (tab) => {
      if (!roles || !Array.isArray(roles)) return null;
      const searchName = tab.toLowerCase();
      const role = roles.find((r) => {
        const name = r.name?.toLowerCase();
        if (searchName === "cfm")
          return name === "cfm" || name === "chief facility manager";
        return name === searchName;
      });
      const id = role?.roleId || role?._id;
      return id
        ? typeof id === "string"
          ? id
          : id.$oid || id.toString()
        : null;
    },
    [roles],
  );

  // Effects
  useEffect(() => {
    if (users) setData(users);
  }, [users]);

  // Logic Handlers
  const handleCreateUser = async () => {
    if (
      !name ||
      !email ||
      !password ||
      !employeeId ||
      (activeTab !== "Admin" && !selectedAcademy)
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isValidEmail(email)) {
      toast.error("Invalid email format");
      return;
    }

    const roleId = getRoleIdByTab(activeTab);
    if (!roleId) {
      toast.error(`Role identifier not found for ${activeTab}`);
      return;
    }

    // Role-specific validation
    const activeTabLower = activeTab.toLowerCase();

    if (activeTabLower === "manager" && !selectedDept?.value) {
      toast.error("Department is required for Manager role");
      return;
    }

    if (
      (activeTabLower === "supervisor" || activeTabLower === "technician") &&
      (!selectedDept?.value || !selectedDiv?.value)
    ) {
      toast.error("Department and Division are required for this role");
      return;
    }

    try {
      // Build user data conditionally based on role
      const userData = {
        name,
        employeeId,
        email,
        password,
        roleId: roleId,
      };

      // Add academyId for all roles except Admin
      if (activeTab !== "Admin") {
        userData.academyId = selectedAcademy?.value;
      }

      // Add departmentId for Manager, Supervisor, and Technician
      if (
        activeTabLower === "manager" ||
        activeTabLower === "supervisor" ||
        activeTabLower === "technician"
      ) {
        userData.departmentId = selectedDept?.value;
      }

      // Add divisionId only for Supervisor and Technician
      if (activeTabLower === "supervisor" || activeTabLower === "technician") {
        userData.divisionId = selectedDiv?.value;
      }

      console.log("🚀 Creating user with data:", userData);
      console.log("📋 Active Tab:", activeTab);
      console.log("🎭 Role ID:", roleId);

      await createUser(userData).unwrap();
      toast.success("User created successfully");
      resetForm();
      setShowModal(false);
      refetchUsers();
    } catch (err) {
      console.error("❌ User creation failed:", err);
      console.error("Error response:", err?.data);

      // Handle different error formats
      let errorMessage = "Failed to create user";
      if (typeof err?.data === "string") {
        // Backend returned plain text error message
        errorMessage = err.data;
      } else if (err?.data?.message) {
        // Backend returned JSON with message field
        errorMessage = err.data.message;
      }

      toast.error(errorMessage);
    }
  };

  const handleUpdateUser = async () => {
    if (!name || !email || !employeeId) {
      toast.error("Required fields missing");
      return;
    }

    try {
      await updateUser({
        id: editUserId,
        name,
        email,
        employeeId,
        roleId: selectedRole?.value,
        academyId: selectedAcademy?.value,
        departmentId: selectedDept?.value,
        divisionId: selectedDiv?.value,
      }).unwrap();

      toast.success("User updated successfully");
      setShowModal(false);
      setIsEditingUser(false);
      setEditUserId(null);
      resetForm();
      refetchUsers();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update user");
    }
  };

  const openEditUserModal = (user) => {
    setIsEditingUser(true);
    setEditUserId(user.userId);
    setName(user.name);
    setEmail(user.email);
    setEmployeeId(user.employeeId);
    setSelectedAcademy(
      academies?.find((a) => (a.academyId || a._id) === user.academyId)
        ? { value: user.academyId, label: getAcademyName(user.academyId) }
        : null,
    );
    setSelectedDept(
      department?.find((d) => (d.departmentId || d._id) === user.departmentId)
        ? {
            value: user.departmentId,
            label: getDepartmentName(user.departmentId),
          }
        : null,
    );
    setSelectedDiv(
      divisions?.find((d) => (d.divisionId || d._id) === user.divisionId)
        ? { value: user.divisionId, label: getDivisionName(user.divisionId) }
        : null,
    );
    setSelectedRole(
      roles?.find((r) => r.roleId === user.role?.roleId)
        ? { value: user.role?.roleId, label: user.role?.name }
        : null,
    );
    setShowModal(true);
  };

  const handleRoleAction = async (e) => {
    e.preventDefault();
    if (!roleNameInput.trim()) return toast.error("Role name is required");
    try {
      if (isEditingRole) {
        await updateRole({
          id: editRoleId,
          name: roleNameInput.trim(),
          description: roleDescriptionInput.trim(),
        }).unwrap();
        toast.success("Role updated");
      } else {
        await createRole({
          name: roleNameInput.trim(),
          description: roleDescriptionInput.trim(),
        }).unwrap();
        toast.success("Role created");
      }
      setShowRoleModal(false);
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const handleDeleteRole = (id) => setDeleteRoleConfirm({ open: true, id });

  const confirmDeleteRole = async () => {
    try {
      await deleteRole(deleteRoleConfirm.id).unwrap();
      toast.success("Role deleted");
      setDeleteRoleConfirm({ open: false, id: null });
    } catch (err) {
      toast.error("Failed to delete role");
    }
  };

  const confirmDeleteUser = async () => {
    try {
      await softDeleteUser(deleteUserConfirm.id);
      await refetchUsers();
      toast.success("User deleted");
      setDeleteUserConfirm({ open: false, id: null });
    } catch (err) {
      toast.error("Failed to delete user");
    }
  };

  const handleAdminResetPassword = async (newPassword) => {
    try {
      await adminResetPassword({
        id: userToReset.userId,
        newPassword,
      }).unwrap();
      toast.success("Password has been reset successfully.");
      setShowResetModal(false);
      setUserToReset(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to reset password");
    }
  };

  const resetForm = () => {
    setName("");
    setEmployeeId("");
    setEmail("");
    setPassword("");
    setSelectedAcademy(null);
    setSelectedDept(null);
    setSelectedDiv(null);
    setSelectedRole(null);
    setEmailError("");
  };

  const filteredData = useMemo(() => {
    if (activeTab === "Role") {
      return (roles || []).filter((item) =>
        matchesSearchTerm(item, searchTerm),
      );
    }
    return (data || []).filter((item) => {
      const roleName = item.role?.name?.toLowerCase();
      if (activeTab === "CFM") {
        if (!isCFMRole(roleName)) return false;
      } else if (roleName !== activeTab.toLowerCase()) {
        return false;
      }
      return (
        (activeTab === "Admin" ||
          matchesAcademyFilter(item, selectedAcademyId)) &&
        matchesSearchTerm(item, searchTerm)
      );
    });
  }, [
    activeTab,
    data,
    roles,
    selectedAcademyId,
    searchTerm,
    matchesAcademyFilter,
    matchesSearchTerm,
    isCFMRole,
  ]);

  const displayedData = filteredData.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);

  const getAcademyName = (id) =>
    academies?.find((a) => (a.academyId || a._id) === id)?.name || "N/A";
  const getDepartmentName = (id) =>
    department?.find((d) => (d.departmentId || d._id) === id)?.name ||
    department?.find((d) => (d.departmentId || d._id) === id)?.departmentName ||
    "N/A";
  const getDivisionName = (id) =>
    divisions?.find((d) => (d.divisionId || d._id) === id)?.name || "N/A";

  const renderTabButtons = () => (
    <div className="flex flex-wrap gap-1 border-b border-border">
      {TABS.map((tab) => (
        <button
          key={tab}
          onClick={() => {
            setActiveTab(tab);
            setCurrentPage(1);
          }}
          className={`relative px-4 py-2.5 text-sm font-medium transition-all rounded-t-lg ${activeTab === tab ? "bg-background text-foreground border-b-2 border-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}
        >
          {tab}
          {activeTab === tab && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold tracking-tight">
            System Administration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage global users, organizational structure, and system roles.
          </p>
        </div>
        <div className="px-6">{renderTabButtons()}</div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {activeTab !== "Admin" && activeTab !== "Role" && (
              <select
                value={selectedAcademyId}
                onChange={(e) => setSelectedAcademyId(e.target.value)}
                className="flex h-10 w-full sm:w-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="all">All Academies</option>
                {academies?.map((a) => (
                  <option
                    key={a.academyId || a._id}
                    value={a.academyId || a._id}
                  >
                    {a.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <button
            onClick={() => {
              if (activeTab === "Role") {
                setIsEditingRole(false);
                setRoleNameInput("");
                setRoleDescriptionInput("");
                setShowRoleModal(true);
              } else {
                setIsEditingUser(false);
                resetForm();
                setShowModal(true);
              }
            }}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add {activeTab}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              {activeTab === "Role" ? (
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">Role Name</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="text-right font-semibold">
                    Actions
                  </TableHead>
                </TableRow>
              ) : (
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">Employee ID</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  {activeTab !== "Admin" && (
                    <TableHead className="font-semibold">Academy</TableHead>
                  )}
                  <TableHead className="text-right font-semibold">
                    Actions
                  </TableHead>
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {displayedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      activeTab === "Role" ? 3 : activeTab === "Admin" ? 4 : 5
                    }
                    className="text-center py-12 text-muted-foreground"
                  >
                    No {activeTab.toLowerCase()} found
                  </TableCell>
                </TableRow>
              ) : (
                displayedData.map((item, i) => (
                  <TableRow key={i}>
                    {activeTab === "Role" ? (
                      <>
                        <TableCell className="font-medium">
                          {item.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.description}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditRoleId(item.roleId);
                                setRoleNameInput(item.name);
                                setRoleDescriptionInput(item.description);
                                setIsEditingRole(true);
                                setShowRoleModal(true);
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-primary transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRole(item.roleId)}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 text-destructive hover:bg-destructive/10 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <div
                            className="flex items-center gap-3 cursor-pointer group"
                            onClick={() =>
                              navigate(`/superadmin/users/${item.email}`)
                            }
                          >
                            <Avatar src={item.image} size={40} />
                            <div>
                              <div className="font-medium group-hover:text-primary transition-colors">
                                {item.name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {item.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{item.employeeId}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.email}
                        </TableCell>
                        {activeTab !== "Admin" && (
                          <TableCell>
                            <Badge variant="outline">
                              {getAcademyName(item.academyId)}
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditUserModal(item);
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-primary transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setUserToReset(item);
                                setShowResetModal(true);
                              }}
                              title="Reset Password"
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-blue-600 transition-colors"
                            >
                              <Lock className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteUserConfirm({
                                  open: true,
                                  id: item.userId,
                                });
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-destructive/10 text-destructive transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center rounded-md h-9 px-4 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center rounded-md h-9 px-4 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Modal - shadcn Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isEditingUser ? "Update Profile" : `Add ${activeTab}`}
            </DialogTitle>
            <DialogDescription>
              {isEditingUser
                ? "Update user information and role assignments"
                : `Create a new ${activeTab.toLowerCase()} account`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeId">Employee ID</Label>
              <Input
                id="employeeId"
                placeholder="e.g., NHDCL@2023"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@nhdcl.bt"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>

            {!isEditingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Initial Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter initial password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            {/* Only show Academy, Department, and Division for non-Admin roles */}
            {activeTab !== "Admin" && (
              <>
                <div className="space-y-2">
                  <Label>Academy</Label>
                  <Select
                    value={selectedAcademy?.value || ""}
                    onValueChange={(val) => {
                      const selected = academies?.find(
                        (a) => (a.academyId || a._id) === val,
                      );
                      setSelectedAcademy({
                        value: val,
                        label: selected?.name,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Academy" />
                    </SelectTrigger>
                    <SelectContent>
                      {academies?.map((a) => (
                        <SelectItem
                          key={a.academyId || a._id}
                          value={a.academyId || a._id}
                        >
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Only show Department and Division for Manager, Supervisor, Technician */}
                {(activeTab === "Manager" ||
                  activeTab === "Supervisor" ||
                  activeTab === "Technician") && (
                  <>
                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Select
                        value={selectedDept?.value || ""}
                        onValueChange={(val) => {
                          const d = department?.find(
                            (x) => (x.departmentId || x._id) === val,
                          );
                          setSelectedDept({
                            value: val,
                            label: d?.name || d?.departmentName,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Department" />
                        </SelectTrigger>
                        <SelectContent>
                          {department
                            ?.filter(
                              (d) => d.academyId === selectedAcademy?.value,
                            )
                            .map((d) => (
                              <SelectItem
                                key={d.departmentId || d._id}
                                value={d.departmentId || d._id}
                              >
                                {d.name || d.departmentName}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Only show Division for Supervisor and Technician */}
                    {(activeTab === "Supervisor" ||
                      activeTab === "Technician") && (
                      <div className="space-y-2">
                        <Label>Division</Label>
                        <Select
                          value={selectedDiv?.value || ""}
                          onValueChange={(val) => {
                            const dv = divisions?.find(
                              (x) => (x.divisionId || x._id) === val,
                            );
                            setSelectedDiv({ value: val, label: dv?.name });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Division" />
                          </SelectTrigger>
                          <SelectContent>
                            {divisions
                              ?.filter(
                                (dv) =>
                                  (dv.departmentId || dv._id) ===
                                  selectedDept?.value,
                              )
                              .map((dv) => (
                                <SelectItem
                                  key={dv.divisionId || dv._id}
                                  value={dv.divisionId || dv._id}
                                >
                                  {dv.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {isEditingUser && (
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={selectedRole?.value || ""}
                  onValueChange={(val) => {
                    const r = roles?.find((x) => x.roleId === val);
                    setSelectedRole({ value: val, label: r?.name });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Change Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles?.map((r) => (
                      <SelectItem key={r.roleId} value={r.roleId}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={isEditingUser ? handleUpdateUser : handleCreateUser}
              disabled={isCreatingUser || isUpdatingUser}
            >
              {isCreatingUser || isUpdatingUser
                ? "Processing..."
                : isEditingUser
                  ? "Update"
                  : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>
              {isEditingRole ? "Update Role" : "Add Role"}
            </DialogTitle>
            <DialogDescription>
              Define the role and its responsibilities
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input
                value={roleNameInput}
                onChange={(e) => setRoleNameInput(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={roleDescriptionInput}
                onChange={(e) => setRoleDescriptionInput(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleRoleAction}>
              {isEditingRole ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialogs */}
      <AlertDialog
        open={deleteUserConfirm.open}
        onOpenChange={(v) =>
          !v && setDeleteUserConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteRoleConfirm.open}
        onOpenChange={(v) =>
          !v && setDeleteRoleConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteRole}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ResetPasswordModal
        show={showResetModal}
        userName={userToReset?.name}
        onClose={() => {
          setShowResetModal(false);
          setUserToReset(null);
        }}
        onReset={handleAdminResetPassword}
        isResetting={isResettingPassword}
      />
    </div>
  );
};

export default SAdminUser;
